import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from 'classnames';

import { useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { LanguagesAbbr, Survey, SurveyQuestion, SurveyQuestionType, SurveyType } from "../../../../../../types/working-model";
import { TypographyItem } from "../../../../../general-ui/typography-item/typography-item";
import Textarea from "../../../../../general-ui/textarea/textarea";
import '../../../../../../scss/live-event/base/session/survey.scss';
import './surveys.scss';
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import { useTranslate } from "../../../../../../i18n/useTranslationModules";
import { GetQuestionStatsForSession, GetQuestionStatsForEvent, GetSurveyStatsForSession, GetSurveyStatsForEvent } from "../../../../../../connection/surveys";
import { OptionalComponent } from "../../../../../../utils/optional-component";

interface SurveyProps {
	content: any;
	survey: Survey;
	lang: LanguagesAbbr;
	template: string;
}

const EditorSurvey: React.FC<SurveyProps> = ({ content, survey, lang, template }) => {
	const sessionId = useTypedSelector(state => state.CreateSessionReducer.workingSession?.session);
	const eventId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.event);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const [viewAllQuestions, setViewAllQuestions] = useState(false);
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const [questionResults, setQuestionResults] = useState<{ totals: Record<number, number>, counts: Record<number, number> }>({ totals: {}, counts: {} });

	const { t } = useTranslate('session');
	const { t: sessionT } = useTranslate(`session.${sessionId}`);

	const maxQuestions = survey?.questions?.length;

	useEffect(() => {
		if (survey.questions.length <= 1) {
			setViewAllQuestions(true);
		}
	}, [survey]);

	useEffect(() => {
		// render actual results for a given question in the editor
		async function getResults() {
			try {
				if (sessionId) {
					const results = await GetQuestionStatsForSession(sessionId, survey.questions[currentQuestion].survey_question, survey.questions[currentQuestion].question_type);
					//totals are keyed on question to account for the view all view
					setQuestionResults({
						totals: { [survey.questions[currentQuestion].survey_question]: results.total },
						counts: results.counts
					});
				} else {
					if (eventId) {
						const results = await GetQuestionStatsForEvent(eventId, survey.questions[currentQuestion].survey_question, survey.questions[currentQuestion].question_type);
						setQuestionResults({
							totals: { [survey.questions[currentQuestion].survey_question]: results.total },
							counts: results.counts
						});
					}
				}
			} catch (e) {
				console.error(e);
			}
		}
		getResults();
	}, [currentQuestion, sessionId, survey.questions, eventId]);

	const displayQuestion = useCallback((question: SurveyQuestion, index: number) => {
		if (!question) return null;
		if (question.question_type === SurveyQuestionType.text) {
			return (
				<React.Fragment key={question.survey_question + '#' + index}>
					<div
						className="survey-results text-result question-container"
					>
						<TypographyItem className={classNames('question-title evt-heading-4', template)}>
							{sessionT(`session.${sessionId}:survey.${survey.survey}.questions.${question?.survey_question}`, (question.title[lang] as string ?? question.title.base))}
						</TypographyItem>
						<div className="survey-text-actions-container">
							<Textarea placeholder={t("Enter your response here")} />
						</div>
					</div>
					{viewAllQuestions && <div className='question-container-spacer-viewAll'></div>}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={question.survey_question + '#' + index}>
				<div
					className="survey-results radio-results question-container"
				>
					<TypographyItem className={classNames('question-title evt-heading-4', template)}>
						{sessionT(`session.${sessionId}:survey.${survey.survey}.questions.${question?.survey_question}`, (question.title[lang] as string ?? question.title.base))}
					</TypographyItem>

					{question.options.map((option) => {
						let prcnt = '0%';
						if (questionResults?.counts?.[option.survey_question_option] !== undefined && questionResults.totals[question.survey_question] !== undefined) {
							prcnt = `${Math.floor((questionResults?.counts?.[option.survey_question_option] / questionResults.totals[question.survey_question]) * 100)}%`;
						}
						return (
							<div
								key={`survey-option-bar-${option.survey_question_option}`}
								className="survey-option-wrapper"
							>
								<div className="survey-option-container">
									<div className="survey-option focus-visible">
										<div className="survey-option-progress-bar-container">
											<div
												className="survey-option-progress-bar"
												style={{
													width: prcnt,
												}}
											/>
										</div>
										<div className="survey-option-content">
											<div className="survey-option-title">
												{sessionT(
													`session.${sessionId}:survey.${survey.survey}.questions.${question?.survey_question}.options.${option.survey_question_option}`,
													(option.label[lang] as string ?? option.label.base)
												)}
											</div>
											<div className="survey-quiz-pill-container">
												{!!option.is_correct && (
													<div className="survey-option-pill">
														Correct answer
													</div>)}
												{survey.survey_type === SurveyType.quiz && (
													<div className="survey-option-pill">
														{prcnt}
													</div>
												)}
											</div>
											{survey.survey_type !== SurveyType.quiz && <div className="survey-option-check">
												<div className="survey-option-pill">
													{prcnt}
												</div>
												{survey.survey_type === SurveyType.survey && (
													<Icon name={ICONS.CHECK_CIRCLE_FILLED} color={COLORS.WHITE} size={16} />
												)}
											</div>}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
				{viewAllQuestions && <div className='question-container-spacer-viewAll'></div>}
			</React.Fragment>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content?.callToAction?.button_name, lang, t, template, questionResults]);

	// rerender list if changes are made to current survey or language
	const renderList = useMemo(() => {
		return survey.questions.map(displayQuestion);
	}, [survey, displayQuestion]);

	// rerender current question if changes are made to current survey
	const renderCollapsed = useMemo(() => {
		return displayQuestion(survey.questions[currentQuestion], currentQuestion);
	}, [survey, currentQuestion, displayQuestion]);

	const handleViewAllQuestions = () => {
		// get all question stats
		async function getResults() {
			// if no token or if collapsing questions, don't call
			if (!token || viewAllQuestions) return;
			try {
				if (sessionId) {
					const results = await GetSurveyStatsForSession(token, sessionId, survey.survey);
					setQuestionResults(results);
				} else {
					if (eventId) {
						const results = await GetSurveyStatsForEvent(token, eventId, survey.survey);
						setQuestionResults(results);
					}
				}
			} catch (e) {
				console.error(e);
			}
		}
		getResults();
		setViewAllQuestions(!viewAllQuestions);
	};

	return (
		<div className="survey-container" key={survey.survey}>
			<TypographyItem className={classNames('survey-title')} tagName="p">
				{sessionT(`session.${sessionId}:survey.${survey.survey}.name`, (survey?.name?.[lang] as string ?? survey?.name?.base))}
			</TypographyItem>
			<div className={'question-list'}>
				<div className={classNames('view-all')}>
					{survey.questions.length > 1 ? (
						<button className='no-style clear' onClick={handleViewAllQuestions}>{viewAllQuestions ? t("Collapse Questions") : t("View All Questions")}</button>
					) : (<></>)}
					{survey.allow_retake && (
						<button className="no-style clear retake-button">{t("Retake Survey")}</button>
					)}
				</div>
				{viewAllQuestions ? renderList : (
					<>
						{renderCollapsed}
						<div className='survey-pagination'>
							<button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("Previous")}</button>
							<div>
								<OptionalComponent display={!viewAllQuestions}>
									<span>{currentQuestion + 1}/{maxQuestions}</span>
								</OptionalComponent>
							</div>
							<button disabled={currentQuestion + 1 === maxQuestions} onClick={() => setCurrentQuestion(currentQuestion + 1)}>{t("Next")}</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default EditorSurvey;